import { Chip } from './ui';

/** 期間フィルタ（§4.5）。今日／今週／今月／全期間／カスタム */
export type Period = 'today' | 'week' | 'month' | 'all' | 'custom';

export interface PeriodRange {
  period: Period;
  /** custom のときだけ使う。YYYY-MM-DD */
  from?: string;
  to?: string;
}

const LABELS: { key: Period; label: string }[] = [
  { key: 'today', label: '今日' },
  { key: 'week', label: '今週' },
  { key: 'month', label: '今月' },
  { key: 'all', label: '全期間' },
  { key: 'custom', label: '期間指定' },
];

/** 期間の下限。all は制限なし */
function startOf(range: PeriodRange): Date | null {
  const now = new Date();
  switch (range.period) {
    case 'today':
      return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    case 'week': {
      // 月曜はじまり
      const day = (now.getDay() + 6) % 7;
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      d.setDate(d.getDate() - day);
      return d;
    }
    case 'month':
      return new Date(now.getFullYear(), now.getMonth(), 1);
    case 'custom':
      return range.from ? new Date(`${range.from}T00:00:00`) : null;
    default:
      return null;
  }
}

/** 期間の上限。custom の to はその日の終わりまで含める */
function endOf(range: PeriodRange): Date | null {
  if (range.period !== 'custom' || !range.to) return null;
  const d = new Date(`${range.to}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return d;
}

/** playedAt がこの期間に入るか */
export function inPeriod(playedAt: string, range: PeriodRange): boolean {
  const t = new Date(playedAt).getTime();
  const start = startOf(range);
  const end = endOf(range);
  if (start && t < start.getTime()) return false;
  if (end && t >= end.getTime()) return false;
  return true;
}

export function periodLabel(range: PeriodRange): string {
  if (range.period === 'custom') {
    if (range.from && range.to) return `${range.from} 〜 ${range.to}`;
    if (range.from) return `${range.from} 以降`;
    if (range.to) return `${range.to} まで`;
    return '期間指定';
  }
  return LABELS.find((l) => l.key === range.period)?.label ?? '';
}

export function PeriodFilter({
  value,
  onChange,
}: {
  value: PeriodRange;
  onChange: (v: PeriodRange) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5">
        {LABELS.map((l) => (
          <Chip
            key={l.key}
            selected={value.period === l.key}
            onClick={() => onChange({ ...value, period: l.key })}
          >
            {l.label}
          </Chip>
        ))}
      </div>

      {value.period === 'custom' && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={value.from ?? ''}
            max={value.to || undefined}
            onChange={(e) => onChange({ ...value, from: e.target.value || undefined })}
            aria-label="開始日"
            className="min-w-0 flex-1 rounded-lg border border-line bg-white px-2.5 py-2 text-[13px] outline-none focus:border-felt"
          />
          <span className="shrink-0 text-xs text-ink-faint">〜</span>
          <input
            type="date"
            value={value.to ?? ''}
            min={value.from || undefined}
            onChange={(e) => onChange({ ...value, to: e.target.value || undefined })}
            aria-label="終了日"
            className="min-w-0 flex-1 rounded-lg border border-line bg-white px-2.5 py-2 text-[13px] outline-none focus:border-felt"
          />
        </div>
      )}
    </div>
  );
}
